import { useState } from 'react';
import { api, fmt, type QueueItem } from '../api.ts';
import SplitTable, { moveSides } from './SplitTable.tsx';

export interface MoveResult {
  splitId: number;
  part: { id: number; jobNumber: string; quantity: number };
  rest: { jobNumber: string; quantity: number };
  nextStage: { name: string; isDispatch: boolean } | null;
}
export default function MovePartDialog({ item, onClose, onDone }: {
  item: QueueItem;
  onClose: () => void;
  onDone: (r: MoveResult) => void;
}) {
  const total = Number(item.ordered_quantity);
  const run = Number(item.production_quantity);
  const next = item.next_stage_name ?? '';
  const [qty, setQty] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const q = Number(qty);
  const whole = Number.isInteger(q) && q > 0;
  const tooMuch = whole && q >= total;
  const valid = whole && !tooMuch;
  const title = `Move part of ${item.job_number} to ${next}`;

  const changes = !valid ? [] : [
    `The new part goes to ${next}`,
    `${item.job_number} stays at ${item.stage_name}`,
    `${fmt(q)} counted as done on ${item.stage_name}`,
  ];

  async function go() {
    if (!valid) return;
    setBusy(true); setError('');
    try {
      const r = await api.post<MoveResult>(`/api/production/job-stages/${item.job_stage_id}/move-part`, { quantity: q });
      onDone(r);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not move the part');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="backdrop" onClick={() => !busy && onClose()}>
      <div className="modal" style={{ width: 420 }} role="dialog" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>{title}</h3>
          <p>{item.customer_name} · {item.box_name} · at {item.stage_name}</p>
        </div>
        <div className="modal-body">
          {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}
          <div className="field" style={{ marginBottom: 0 }}>
            <label htmlFor="move-qty">Quantity to move</label>
            <input id="move-qty" type="number" min="1" step="1" autoFocus value={qty}
                   onFocus={(e) => e.target.select()}
                   onChange={(e) => setQty(e.target.value)}
                   onKeyDown={(e) => { if (e.key === 'Enter') void go(); }} />
            <div className="hint">
              {tooMuch ? `Less than ${fmt(total)}. For all of it, use Move to ${next}.` : run > total ? `Run ${fmt(run)} · ordered ${fmt(total)}` : `of ${fmt(total)}`}
            </div>
          </div>
          {valid && <SplitTable sides={moveSides(item.job_number, total, run, q)} ups={item.ups} />}
          {changes.length > 0 && (
            <ul className="undo-list" style={{ marginTop: 16 }}>
              {changes.map((c) => <li key={c}>{c}</li>)}
            </ul>
          )}
        </div>
        <div className="modal-foot">
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" onClick={() => void go()} disabled={busy || !valid}>
            {busy ? 'Moving…' : valid ? `Move ${fmt(q)} to ${next}` : `Move to ${next}`}
          </button>
        </div>
      </div>
    </div>
  );
}
